<template>
  <div class="FormDropdown">
    <Select
      v-model="modelValue"
      class="FormDropdown__select"
      v-bind="{
        name,
        options,
        isRequired
      }"
      @focus="haptic(ImpactStyle.Light)"
      @change="haptic()"
    />
    <div class="FormDropdown__arrowButton">
      <Icon
        class="FormDropdown__arrowIcon"
        name="triangleDown"
      />
    </div>
  </div>
</template>

<script lang="ts" generic="T" setup>
import { FormDropdownOption } from "@/components/FormDropdown.types";
import Icon from "@/components/Icon.vue";
import Select from "@/components/Select.vue";
import haptic from "@/lib/utils/platform/haptic";
import { ImpactStyle } from "@capacitor/haptics";

const modelValue = defineModel<T>({
  required: true,
});

defineProps<{
  name: string,
  options: Array<FormDropdownOption<T>>,
  isRequired?: boolean,
}>();
</script>

<style lang="scss" scoped>
.FormDropdown {
  --selectHeight: 48px;
  --highlightWidth: 3px;
  --outlineWidth: 2px;
  --offsetWidth: calc(var(--highlightWidth) + var(--outlineWidth));
  --borderRadius: calc(6px + var(--offsetWidth));
  --outlineColorDisabled: var(--themeDarkTransparent500);
  --backgroundColorDisabled: var(--themeLightTransparent600);
  --textColorDisabled: var(--themeDarkTransparent500);
  --arrowColorDisabled: var(--themeDarkTransparent400);
  --placeholderColorDisabled: var(--themeDarkTransparent400);
  --highlightColorDisabled: var(--themeLightTransparent400);
  --buttonBackgroundColorDisabled: var(--themeDarkTransparent150);
  --transitionDuration: 350ms;

  [data-theme="light"] & {
    --highlightWidth: 0px;
    --outlineColorDisabled: var(--themeColorNeutral400);
    --backgroundColorDisabled: var(--themeColorNeutral0);
    --textColorDisabled: var(--themeColorNeutral500);
    --arrowColorDisabled: var(--themeColorNeutral500);
    --placeholderColorDisabled: var(--themeColorNeutral500);
    --highlightColorDisabled: var(--themeColorNeutral0);
    --buttonBackgroundColorDisabled: var(--themeColorNeutral150);
  }

  position: relative;

  height: min-content;

  border-radius: var(--borderRadius);
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);

  transition: var(--transitionDuration);
  transition-property: color, background-color;

  &::after {
    position: absolute;
    inset: 0;

    border-radius: inherit;
    outline: var(--outlineWidth) solid var(--themeColorNeutral900);
    outline-offset: calc(-1 * var(--offsetWidth));

    transition: outline-color var(--transitionDuration);

    pointer-events: none;
    content: "";
  }

  &:has(select:disabled) {
    background-color: var(--backgroundColorDisabled);
    color: var(--textColorDisabled);

    &::after {
      outline-color: var(--outlineColorDisabled);
    }
  }
}

.FormDropdown__select {
  width: 100%;
  height: calc(var(--selectHeight) + 2 * var(--highlightWidth));
  padding: 0 calc(12px + var(--selectHeight) + var(--offsetWidth)) 0 calc(12px + var(--offsetWidth));

  border-radius: inherit;
  color: inherit;
  box-shadow: 0 0 0 var(--highlightWidth) transparent inset;

  transition: var(--transitionDuration);
  transition-property: color, box-shadow;

  [data-theme="light"] & {
    box-shadow: 0 0 0 var(--highlightWidth) transparent;
  }

  &::placeholder {
    transition: color var(--transitionDuration);
  }

  &:disabled {
    color: inherit;

    &::placeholder {
      color: var(--themeDarkTransparent400);
    }
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:focus-visible {
    box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorSecondary) inset;

    [data-theme="light"] & {
      box-shadow: 0 0 0 3px var(--themeColorSecondary);
    }
  }
}

.FormDropdown__arrowButton {
  --arrowButtonRadius: calc(var(--borderRadius) - (var(--highlightWidth) + var(--outlineWidth)));
  position: absolute;
  inset: var(--highlightWidth) var(--highlightWidth) var(--highlightWidth) auto;

  display: grid;
  place-items: center;
  width: calc(var(--selectHeight) - var(--outlineWidth));
  padding-right: var(--outlineWidth);

  border-radius: 0 var(--borderRadius) var(--borderRadius) 0;
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  pointer-events: none;
  transition: var(--transitionDuration);
  transition-property: background-color, color;

  select:disabled + & {
    background-color: var(--buttonBackgroundColorDisabled);
    color: var(--arrowColorDisabled);
  }
}

.FormDropdown__arrowIcon {
  --iconSize: 14px;
}
</style>
